import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/StatusBadge";
import { Download, ChevronLeft, Sparkles } from "lucide-react";

function fmt(iso) { return iso ? new Date(iso).toLocaleString() : "—"; }

export default function Receipt() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  useEffect(() => { api.getReceipt(id).then(setR); }, [id]);

  if (!r) return <Card className="p-8 text-sm text-muted-foreground">Loading…</Card>;

  const p = r.payload || {};
  const ev = p.event;
  const inc = p.incident;
  const decisions = p.decisions || [];
  const residual = p.residual_risks || [];

  const downloadJSON = () => {
    const blob = new Blob([JSON.stringify(r, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${r.id}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="receipt-page">
      <Link to="/receipts" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-receipts">
        <ChevronLeft className="h-4 w-4" /> Back to receipts
      </Link>

      <Card className="receipt-paper p-8 sm:p-10 border-border space-y-6 font-mono-ui">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-eyebrow text-muted-foreground">Evidence Receipt</div>
            <div className="text-2xl tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>ClubCierge</div>
          </div>
          <div className="text-right text-xs">
            <div className="text-muted-foreground">Receipt ID</div>
            <div className="text-foreground" data-testid="receipt-id">{r.id}</div>
            <div className="text-muted-foreground mt-1">Generated</div>
            <div>{fmt(r.created_at)}</div>
          </div>
        </div>

        <div className="dashed-divider" />

        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <Row k="Subject" v={ev ? ev.title : inc ? `Incident ${inc.id}` : "—"} />
          <Row k="Kind" v={ev ? ev.kind : "incident"} />
          {ev && <Row k="When" v={`${fmt(ev.start_at)} → ${fmt(ev.end_at)}`} />}
          {ev && <Row k="Location" v={ev.location} />}
          {inc && <Row k="Raised by" v={`${inc.raised_by} · ${fmt(inc.raised_at)}`} />}
        </div>

        {ev && ev.approval_mode && (
          <div className="rounded-md p-3" style={{ backgroundColor: ev.approval_mode === "with_mitigation" ? "var(--rag-amber-bg)" : "var(--rag-green-bg)" }} data-testid="receipt-approval-mode">
            <div className="tracking-eyebrow" style={{ color: ev.approval_mode === "with_mitigation" ? "var(--rag-amber-text)" : "var(--rag-green-text)" }}>
              {ev.approval_mode === "with_mitigation" ? "Approved with mitigation" : "Approved — no open exceptions"}
            </div>
            {ev.mitigation_summary && (
              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} className="text-[12px] mt-1 text-foreground">
                {ev.mitigation_summary}
              </div>
            )}
            {ev.approval_notes && (
              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} className="text-[11px] mt-1 text-muted-foreground italic">
                Conditions: {ev.approval_notes}
              </div>
            )}
          </div>
        )}

        {(inc || (p.linked_incidents && p.linked_incidents.length > 0)) && (
          <>
            <div className="dashed-divider" />
            <div>
              <div className="text-[10px] tracking-eyebrow text-muted-foreground mb-2">What was reported</div>
              {(inc ? [inc] : p.linked_incidents).map((i) => (
                <div key={i.id} className="mb-3 text-sm" data-testid={`receipt-incident-${i.id}`}>
                  <div className="italic" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>“{i.text}”</div>
                  <AIClassif c={i.ai_classification} />
                </div>
              ))}
            </div>
          </>
        )}

        <div className="dashed-divider" />

        <div>
          <div className="text-[10px] tracking-eyebrow text-muted-foreground mb-2">Human decisions ({decisions.length})</div>
          {decisions.length === 0 && <div className="text-sm text-muted-foreground" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>No decisions recorded.</div>}
          <ol className="space-y-2 text-xs">
            {decisions.map((d) => (
              <li key={d.id} className="flex gap-3" data-testid={`receipt-decision-${d.id}`}>
                <span className="text-muted-foreground whitespace-nowrap">{fmt(d.timestamp)}</span>
                <span>
                  <b style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{d.reviewer_name}</b> ({d.reviewer_role}) → {d.action}
                  {d.notes && <div className="text-muted-foreground" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>“{d.notes}”</div>}
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="dashed-divider" />

        <div className="grid sm:grid-cols-2 gap-4">
          <Row k="Final status" v={p.final_status} testId="receipt-final-status" />
          <Row k="Residual risks" v={residual.length === 0 ? "None outstanding" : `${residual.length} open`} testId="receipt-residual-count" />
        </div>
        {residual.length > 0 && (
          <ul className="text-xs space-y-1">
            {residual.map((x) => (
              <li key={x.id}>· {x.title} <span className="text-muted-foreground">[{x.kind} · {x.severity}]</span></li>
            ))}
          </ul>
        )}

        <div className="dashed-divider" />

        <div className="flex items-end justify-between gap-4">
          <div className="text-[10px] text-muted-foreground leading-relaxed max-w-md" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            A record of how the club identified, reviewed and resolved (or consciously accepted) this operational item. Share with committee, insurers or event organisers as needed.
            AI recommends · Authorised humans decide · The club keeps the receipt.
          </div>
          <div className="text-right text-[10px]">
            <div className="text-muted-foreground">Hash</div>
            <div data-testid="receipt-hash">{r.hash}</div>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={downloadJSON} data-testid="download-receipt-json">
          <Download className="h-4 w-4 mr-1.5" /> Download JSON
        </Button>
      </div>
    </div>
  );
}

function Row({ k, v, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] tracking-eyebrow text-muted-foreground">{k}</div>
      <div className="text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{v ?? "—"}</div>
    </div>
  );
}

function AIClassif({ c }) {
  if (!c) return null;
  return (
    <div className="mt-2 text-[11px] flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground">
      <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />AI · advisory</span>
      <span>category: <b className="text-foreground">{c.category}</b></span>
      <span>severity: <b className="text-foreground">{c.severity}</b></span>
      <span>escalation: <b className="text-foreground">{(c.escalation_route || []).join(", ")}</b></span>
    </div>
  );
}
